<template>
  <!--
    【P249】全平台统一消息面板（销售 / 市场 / 财务 / 运营 四端共用）

    统一的能力（源自销售端，已抽象）：
      · 四 Tab 分类（由父级通过 tabs 传入，顺序与文案可自定义）
      · 红色气泡角标（仅 count > 0 时显示）
      · 批量条（**按 Tab 粒度**，见下方 P301 说明）
      · 未读红点、标签、时间
      · 描述完整展示（★ 不加 line-clamp / text-overflow，多行自适应）
      · 底部「去处理」入口（showAction 时显示，点击 emit action）
      · 批量二次确认用自研 ConfirmDialog 组件（不用原生 $confirm）
      · 手机端易操作：Tab 等宽铺满、触摸区 ~46px、吸顶、按压反馈

    【P301 全站统一改造】2026-10-11
      ① 移除顶部右上角「全部已读」按钮（showReadAll 默认 false）
      ② Tab 固定 4 个，顺序：未读 → 待处理 → 待阅驳回 → 全部
      ③ 批量能力改为按 Tab 粒度（判定唯一出口 = computed `batchOn`）：
           · 待处理 / 待阅驳回 → ❌ 永远禁止批量，仅单点进详情
           · 未读    → ✅ 全选+批量已读，**仅四大岗位**（后端下发 isCoreRole）
           · 全部    → ✅ 全选+批量删除，全角色通用
      ④ 新增可选能力开关（销售端保留自研特性）：
           showDetail（详情弹层）/ jumpable（精准跳转）/ bizMap（业务徽标）/ showChips（正文标签）

    父级只需传：tabs / list / tab 当前值 / isCoreRole，并监听事件。
  -->
  <div class="mp" :class="{ 'mp--paged': paged }">
    <!-- 顶部条（可选，运营/市场/财务都有） -->
    <!-- 【P301 全站统一】★ 移除右上角「全部已读」按钮 —— 全站不再存在该入口。
         「未读 Tab 批量已读」已由批量条承接（且仅四大岗位有），
         不再需要顶部这个独立入口。
         如需临时恢复，传 showReadAll=true 即可（默认关闭）。 -->
    <div class="mp-top" v-if="showBack" @click="$emit('back')">
      <van-icon name="arrow-left" class="mp-top__back" />
      <span class="mp-top__t">{{ title }}</span>
      <span class="mp-top__r" v-if="showReadAll" @click.stop="$emit('readAll')">{{ readAllText }}</span>
    </div>

    <!-- 分类 Tab：等宽铺满，手机端友好 -->
    <div class="mp-tabs" v-if="tabs.length > 1">
      <div v-for="t in tabs" :key="t.value"
           class="mp-tab" :class="{ 'mp-tab--on': tab === t.value }"
           @click="onSwitchTab(t.value)">
        <span class="mp-tab__tx">{{ t.label }}</span>
        <em class="mp-tab__dot" v-if="t.count > 0">{{ t.count > 99 ? '99+' : t.count }}</em>
      </div>
    </div>

    <!-- 批量二次确认（全站统一弹窗） -->
    <ConfirmDialog
      v-model="dialogConfirm"
      :title="confirmTitle"
      :desc="confirmDesc"
      :loading="busy"
      :loading-text="busyText"
      :confirm-text="confirmBtnText"
      @confirm="doBatch"
    />

    <!-- 批量操作条
         【P301 全站统一】★ 批量能力改为**按 Tab 粒度**决定，不再由父级一个 batchEnabled 一把梭：
           · Tab「待处理」  → ❌ 永远禁止任何批量（仅单点进详情）
           · Tab「待阅驳回」→ ❌ 永远禁止任何批量（仅单点进详情）
           · Tab「未读」    → ✅ 仅**四大岗位**（销售/市场/财务/运营）有 全选 + 批量已读
           · Tab「全部」    → ✅ 全角色通用：全选 + 批量删除
         判定入口统一到 computed `batchOn` / `batchHasSelect`。 -->
    <div class="mp-batch" v-if="batchOn && list.length">
      <label class="mp-batch__all" v-if="batchHasSelect">
        <input type="checkbox" :checked="pickedAll" @change="onToggleAll" />
        <span>全选</span>
      </label>
      <span class="mp-batch__n">已选 <b>{{ picked.length }}</b> / {{ list.length }} 条</span>
      <button class="mp-batch__btn" :disabled="!picked.length || busy" @click="onAskBatch">
        {{ busy ? busyText : batchBtnText }}
      </button>
    </div>

    <!-- 列表 -->
    <!-- 【P297】★★ 修「下拉刷新后一直卡在加载中」
         原写法：`:value="refreshing" @input="onRefreshing"`
         —— 这是**单向**的：`@input` 只能写进内部 refreshingProxy（模板里从未使用），
            父级复位 `refreshing=false` 也传不回 Vant。
         而 Vant 的 PullRefresh 是 `value` 驱动状态机的（watch value → setStatus）：
           mounted 时 value=false ⇒ status='normal'
           下拉松手 → onTouchEnd 里 `setStatus(headHeight, true)` ⇒ status='loading'，
                      并 `$emit('input', true)`（无人接收）+ `$emit('refresh')`
           ★ 此后 Vant 自身的 value **永远是 false**（我们从没把它写成 true），
             watch 永不触发 setStatus(0) ⇒ **status 永久停在 'loading'**
             ⇒ 页面「加载中…」不消失，且组件 `touchable=false`（status==='loading'）
                ⇒ 再也拉不动。
         正确做法：用真正的 v-model 双向绑定，把内部值同步给 Vant，
         同时通过 input 事件把「开始/结束刷新」如实回抛给父级。 -->
    <!-- ★★ paged=true 时，pull-refresh 自己必须是滚动容器。
         Vant 的 `.van-pull-refresh{overflow:hidden}` 是硬编码的**同属性**样式，
         scoped CSS 里的后代选择器经 postcss 转换后 overflow-y 会被裁掉（实测只留 overscroll），
         `!important` 也不可靠 ⇒ 这里改用**行内 style**（优先级最高，一定生效）。
         van-list 的 getScroller 从 .van-list 自身向上找，
         第一个 overflow-y ∈ {scroll,auto,overlay} 的祖先就是它 ⇒ 触底才会被 detect。 -->
    <van-pull-refresh v-model="innerRefreshing" @refresh="onPanelRefresh"
                      :style="paged ? scrollStyle : null">
      <!-- 【P304·上拉分页】★ 原组件内**只有 pull-refresh、没有 van-list**，
           ⇒ 父级即使实现 page 累加也没有「触底」事件源，分页永远接不上。
           做法：把列表内容抽成 `<component :is="listWrapTag">`，
             · paged=false（默认）→ 渲染普通 <div>，**四端现有行为 100% 不变**；
             · paged=true          → 渲染 <van-list>，由父级驱动 page / finished。
           注意 van-list 的 loading 必须用 **独立的 listLoading prop**，
           不能复用 pull-refresh 的 loading —— 后者是父级单向传下来的只读值，
           当成 v-model 用会让 van-list 状态机与父级互相打架（老坑，见 P297）。 -->
      <!-- paged=false 时 listWrapAttrs 为 {}，这里是普通 <div>；
           paged=true 时是 <van-list>，属性由 listWrapAttrs 注入。
           ★ Vue2 的 v-bind 展开对 `on:{load}` 支持不稳（那是 Vue3 语法），
             所以 @load 必须**显式写出来**才可靠 —— 两者并存不冲突：
             显式 @load 保证事件一定到，listWrapAttrs 只负责 loading/finished。 -->
      <component :is="listWrapTag" v-bind="listWrapAttrs" @load="onReachBottom"
                 :style="paged ? listScrollStyle : null">
      <div class="mp-list">
        <div v-if="err" class="mp-err">{{ err }}</div>

        <div class="mp-card" v-for="m in list" :key="keyOf(m)"
             :class="{ 'mp-card--invalid': isInvalid(m) }"
             @click="onCardClick(m)">
          <label class="mp-card__pick" v-if="batchOn" @click.stop>
            <input type="checkbox" :checked="isPicked(m)" @change="onToggleOne(m)" />
          </label>
          <!-- 未读红点 -->
          <span class="mp-card__dot" :class="{ 'mp-card__dot--on': !isRead(m) }"></span>
          <div class="mp-card__body">
            <div class="mp-card__top">
              <span class="mp-card__tag" :class="tagClass(m)">{{ roleText(m) }}</span>
              <span class="mp-card__scene">{{ sceneText(m) }}</span>
              <!-- 【P298】业务状态标签：通知 / 待处理 / 已处理 / 已驳回 / 已失效 -->
              <span class="mp-st" :class="statusOf(m).cls">{{ statusOf(m).text }}</span>
              <span class="mp-card__time">{{ timeText(m) }}</span>
            </div>
            <!-- ★ 描述完整展示：white-space:pre-wrap，不限行数 -->
            <div class="mp-card__text">{{ textOf(m) }}</div>
            <!-- 【P298】正文结构化标签行：日期/时段/金额/折扣 -->
            <div class="mp-card__chips" v-if="showChips && chipsOf(m).length">
              <span v-for="(c, ci) in chipsOf(m)" :key="ci"
                    class="mp-chip" :class="{ 'mp-chip--key': c.key }">{{ c.text }}</span>
            </div>
            <!-- 底部「去处理」 -->
            <div class="mp-card__act" v-if="showAction" @click.stop="$emit('action', m)">
              <span>{{ actionText }}</span>
              <van-icon name="arrow" class="mp-card__act-ico" />
            </div>
          </div>
        </div>

        <div class="mp-empty" v-if="!loading && !list.length">
          <div class="mp-empty__i">{{ emptyIcon }}</div>
          <div class="mp-empty__t">{{ err || emptyText }}</div>
        </div>
        <div class="mp-empty" v-if="loading"><div class="mp-empty__t">加载中…</div></div>
      </div>
      <!-- 【P304】仅 paged=true 时显示「没有更多了」；普通 div 模式下不渲染 -->
      <div v-if="paged && finished && list.length" class="mp-nomore">没有更多了</div>
      </component>
    </van-pull-refresh>

    <div class="mp-foot" v-if="list.length">
      <span>{{ footHint }}</span>
      <span>共 {{ list.length }} 条</span>
    </div>

    <!-- 【P301】可选：消息详情弹层（销售端保留该能力；其他端默认不启用）
         待处理 / 待阅驳回 两个 Tab「单点进详情后自动完成阅读/处理」即依赖它。
         jumpFn 由父级注入（销售端传自己的 jumpMsg）。 -->
    <van-popup v-if="showDetail" v-model="detailShow" round position="bottom"
               class="mp-detail-pop" :style="{ maxHeight: '74%' }">
      <div class="mp-detail" v-if="detailRow">
        <div class="mp-detail__hd">
          <span class="mp-card__tag" :class="tagClass(detailRow)">{{ roleText(detailRow) }}</span>
          <span class="mp-st" :class="statusOf(detailRow).cls">{{ statusOf(detailRow).text }}</span>
          <span class="mp-detail__time">{{ timeText(detailRow) }}</span>
          <van-icon name="cross" class="mp-detail__close" @click="detailShow = false" />
        </div>
        <div class="mp-detail__title">{{ textOf(detailRow) }}</div>
        <div class="mp-detail__ft">
          <van-button block plain @click="detailShow = false">关闭</van-button>
          <van-button v-if="detailRow && detailJumpable(detailRow)" block type="info" @click="onDetailJump">去处理</van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import ConfirmDialog from './ConfirmDialog.vue'

/* 消息 scene → 中文名（各端共用一张表，避免各端各写一份） */
const SCENE = {
  room_order_todo: '会议室待办', room_order_change: '订单变更',
  room_order_cancel: '已取消', room_order_cc: '抄送', room_order_finish: '已完成',
  room_order_timeout: '超时未处理',   /* 【P416】待办超时提醒（发给运营负责人） */
  approval: '审批', payment: '回款', refund: '退款', contract: '合同',
  order: '订单', customer: '客户', opportunity: '商机',
}
const EMPTY = {
  pending: '没有待处理的消息',
  confirmed: '还没有已确认的记录',
  unread: '没有未读消息',
  cc: '暂无抄送',
  all: '暂无消息',
  reject: '没有待阅驳回的消息',
}
/* 【P301 全站统一】Tab 顺序固定：未读 → 待处理 → 待阅驳回 → 全部 */
const TAB_ORDER = ['unread', 'pending', 'reject', 'all']

export default {
  name: 'MessagePanel',
  components: { ConfirmDialog },
  props: {
    title:        { type: String,  default: '消息' },
    showBack:     { type: Boolean, default: true },
    /* 【P301 全站统一】顶部「全部已读」默认关闭（全站移除该入口） */
    showReadAll:  { type: Boolean, default: false },
    readAllText:  { type: String,  default: '全部已读' },
    /* [{ value, label, count }] —— 顺序固定：未读 / 待处理 / 待阅驳回 / 全部 */
    tabs:         { type: Array,   default: () => [] },
    tab:          { type: String,  default: '' },
    list:         { type: Array,   default: () => [] },
    loading:      { type: Boolean, default: false },
    refreshing:   { type: Boolean, default: false },
    busy:         { type: Boolean, default: false },
    err:          { type: String,  default: '' },

    /* ============ 【P301】批量能力：按 Tab 粒度 + 四岗位开关 ============
     * batchEnabled 保留为「总开关」，实际是否显示由 computed `batchOn` 决定。
     * 【P301 全站统一 · 批量能力矩阵】
     *   Tab 未读     ：全选 + 批量已读 → **仅四大岗位**（isCoreRole=true），其他角色无
     *   Tab 待处理   ：❌ 永远禁止批量（仅单点进详情）
     *   Tab 待阅驳回 ：❌ 永远禁止批量（仅单点进详情）
     *   Tab 全部     ：全选 + 批量删除 → 全角色通用
     * isCoreRole 由**后端能力位下发**（父级透传），不在前端硬编码岗位判断。
     * batchAction：read（批量已读）/ delete（批量删除）。
     */
    batchEnabled: { type: Boolean, default: true },
    isCoreRole:   { type: Boolean, default: false },   // 后端下发的「四大岗位」能力位
    /* 【P308】「待处理」Tab 是否允许批量 —— 默认 false。
       P301 全站口径是「待处理/待阅驳回 永禁批量」；
       但运营端历史上就有「待处理中 → 批量『我知道了』」（P232/P281 的 messageConfirmBatch），
       用户 10-12 要求恢复 ⇒ 运营端传 true 即可，**其余三端行为不变**。 */
    allowPendingBatch: { type: Boolean, default: false },
    batchAction:  { type: String,  default: 'read' },   // read | delete
    /* 底部「去处理」入口 */
    showAction:   { type: Boolean, default: false },
    actionText:   { type: String,  default: '去处理' },
    emptyIcon:    { type: String,  default: '✉' },
    /* 【P298】业务类型徽标映射 { page_api: {text,bg,color} }，父端可覆盖 */
    bizMap:       { type: Object, default: () => ({}) },
    /* 【P298】是否展示正文结构化标签行（对齐销售端 msgChips） */
    showChips:    { type: Boolean, default: true },
    /* ============ 【P301】可选能力开关（销售端保留自研特性）============
     * showDetail  启用详情弹层（待处理/待阅驳回「单点进详情」依赖它）
     * detailJumpable(row)  该条是否可「去处理」（返回布尔）
     * onJump(row)          点「去处理」时通知父级执行跳转
     */
    showDetail:       { type: Boolean, default: false },
    detailJumpable:   { type: Function, default: () => false },
    onJump:           { type: Function, default: null },

    /* ============ 【P304】上拉分页（可选，默认关闭以保持另三端零影响）============
     * paged        是否启用上拉分页（true 时列表容器换成 van-list）
     * finished     是否已到底（父级判定：后端返回条数 < pageSize 或 已取满 total）
     * listLoading  ★ 必须是**独立**的 loading，不要复用 pull-refresh 的 loading：
     *               van-list 是 v-model:loading 双向状态机，复用只读 prop 会卡死。
     * 父级监听 @load-more 触发 page+1 请求。 */
    paged:            { type: Boolean, default: false },
    finished:         { type: Boolean, default: false },
    listLoading:      { type: Boolean, default: false },
    finishedText:     { type: String,  default: '没有更多了' },
  },
  data() {
    /* 【P297】refreshingProxy → innerRefreshing
       ★ 它是 Vant 的 v-model 真实载体：下拉时被 Vant 写成 true，
         请求结束（父级 refreshing 变 false）时同步回 false，
         Vant 的 watch 才会 setStatus(0) 把头部弹回、status 复位 normal。
       （原名 refreshingProxy 且只写不读，等于没接上。） */
    return { picked: [], dialogConfirm: false, innerRefreshing: false,
             detailShow: false, detailRow: null }
  },
  computed: {
    /* ---------- 【P301 全站统一】批量能力矩阵（唯一判定出口） ----------
     *   pending（待处理）  / reject（待阅驳回）→ ❌ 永远禁止任何批量
     *   unread（未读）                      → ✅ 仅四大岗位（isCoreRole）
     *   all   （全部）                      → ✅ 全角色通用（批量删除）
     * 其余 Tab 一律不显示批量条（未来新增 Tab 也不会误开）。 */
    batchOn() {
      if (!this.batchEnabled || !this.list.length) { return false; }
      if (this.tab === 'unread')  { return !!this.isCoreRole; }
      if (this.tab === 'all')    { return true; }
      /* 【P308】待处理 Tab 的批量是**按端开启**的（运营端要恢复「批量我知道」，
         销售/市场/财务端仍按 P301 全站口径「待处理永禁批量」）。
         ⇒ 用 allowPendingBatch 显式放行，默认 false 保证其余端零影响。 */
      if (this.tab === 'pending') { return !!this.allowPendingBatch; }
      /* reject / 其它 → 禁止批量 */
      return false;
    },
    /* 未读批量动作 = 批量已读；全部 Tab = 批量删除（由父级传 batchAction 决定，此处兜底） */
    batchHasSelect() { return this.batchOn; },

    batchMode()   { return this.batchOn; },   // ★ 兼容旧字段（父级若仍读它）
    pickedAll()   {
      const n = this.list.length;
      return n > 0 && new Set(this.picked).size === n;
    },
    batchBtnText() {
      /* 【P308】confirm 动作的按钮文案改为「批量我知道」（运营端历史文案）。
         其它端 pending 不开批量 ⇒ 不会看到这一行，不影响全站口径。 */
      if (this.batchAction === 'confirm') { return '批量我知道'; }
      return { read: '批量已读', delete: '批量删除', confirm: '批量确认' }[this.batchAction] || '批量处理';
    },
    busyText() {
      return { read: '处理中', delete: '删除中', confirm: '处理中' }[this.batchAction] || '处理中';
    },
    confirmTitle() {
      /* 【P308】confirm 文案改回运营端历史语义「我知道」 */
      if (this.batchAction === 'confirm') { return '确认标记「我知道了」'; }
      return {
        read:    '确认标记已读',
        delete:  '确认彻底删除',
        confirm: '确认批量确认',
      }[this.batchAction] || '请确认';
    },
    confirmBtnText() {
      return { read: '标记已读', delete: '删除', confirm: '确认' }[this.batchAction] || '确定';
    },
    confirmDesc() {
      const n = this.picked.length;
      if (this.batchAction === 'delete') {
        return '确定彻底删除所选的 ' + n + ' 条消息吗？删除后不可恢复。';
      }
      if (this.batchAction === 'confirm') {
        /* 【P308】恢复运营端历史文案「我知道了」 */
        return '确定将所选的 ' + n + ' 条待处理消息标记为「我知道了」吗？确认后将同步处理对应单据。';
      }
      return '确定将所选的 ' + n + ' 条消息标记为已读吗？';
    },
    emptyText() {
      return EMPTY[this.tab] || '暂无消息';
    },
    /* 【P304】★ van-list **自身**的滚动样式。
           读 vant/lib/utils/dom/scroll.js 的 getScroller 源码确认：
             它从 `node = el`（= van-list 的 $el）**自身开始**向上找，
             第一个 overflowY 命中 /scroll|auto|overlay/i 的元素即返回。
           ⇒ 让 van-list 自己 overflowY:auto，getScroller 第一步就命中自己，
             scrollTop 可变、check() 才能算准触底。
           用行内 style：Vant/父级 CSS 都可能覆盖，scoped 里的后代选择器经 postcss
           还会丢属性（实测 overflow-y 被裁掉只剩 overscroll）。 */
    listScrollStyle() {
      return {
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        /* ★ 高度必须**显式约束**为 100%：
           只写 flex:1 1 auto 在 .van-pull-refresh__track（默认 flex:0 1 auto）里不生效，
           van-list 会被内容撑到 scrollHeight 等高（实测 h=1694 / clientHeight=1694）
           ⇒ scrollHeight === clientHeight ⇒ scrollTop 恒 0 ⇒ 滑不动、触底永不触发。
           父级 track 已由 pull-refresh 的 flex 撑出一屏，height:100% 即取那一屏。 */
        height: '100%',
        flex: '1 1 auto',
        minHeight: '0',
        boxSizing: 'border-box'
      };
    },

    /* 【P304】paged 时的滚动容器行内样式（优先级最高，压过 Vant 的 overflow:hidden）。
           高度链路（实测踩坑，必须逐层都约束，否则 scrollHeight===clientHeight 滑不动）：
             .mp (flex:1,min-h:0)
               └ .van-pull-refresh  本行内样式撑满一屏（height:100% + overflowY:auto）
                   └ __track          Vant 硬编码 height:100% ⇒ 跟随一屏
                       └ van-list     listScrollStyle height:100% ⇒ 拿到一屏，内容超出即可滚 */
    scrollStyle() {
      return {
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: '0'
      };
    },

    /* 【P304】列表容器绑定：paged 时给 van-list，关闭时给普通 div。
       ★ van-list 用 `v-bind="..."` 展开属性，因此这里给**驼峰** key（Vant2 会转 kebab）。
         防并发竞态由父级用 msgListLoading / msgFinished 两道闸门负责。 */
    listWrapTag() {
      return this.paged ? 'van-list' : 'div';
    },
    listWrapAttrs() {
      if (!this.paged) { return {}; }
      return {
        loading: this.listLoading,
        finished: this.finished,
        finishedText: this.finishedText,
        /* ★ immediate-check 必须**关闭**。
           Vant 默认 true：列表渲染后只要「内容不足一屏」就会立刻 check() 触发 load，
           本项目 msgPageSize=20 且卡片较矮，首屏放不满 20 条
           ⇒ 会在用户**还没滑动**时就连翻到底，把 95 条一次性全拉下来（实测如此）。
           关掉后：只在用户真实滚动触底时才请求下一页，符合「上滑加载更多」的预期。
           ⚠ 副作用：切 Tab 后若首屏确实不满一屏也不会自动补页，
               用户需微滑一下才继续加载 —— 这是上滑分页的正常行为。 */
        immediateCheck: false,
      };
    },
    footHint() {
      if (this.batchAction === 'confirm' && this.tab === 'pending') return '勾选后点「批量确认」';
      if (this.batchAction === 'delete') return '勾选后可批量删除（不可恢复）';
      return '';
    },
  },
  watch: {
    /* 【P304】immediateCheck 关闭后 van-list 不会自检，必须在数据到位后手动 check() 一次，
       否则滚动监听可能未挂上（表现为「滑到底没反应」）。
       ★ 延后到 nextTick：此时 DOM 高度才最终确定，check 算得准。
       ★ 同时承担原职责：切数据时清空勾选，防跨页误操作。 */
    list() {
      this.picked = [];
      if (this.paged) { this.$nextTick(this.recheckList); }
    },

    /* 【P304】切 Tab / 换分类都清空勾选 + 重新自检 */
    tab() {
      this.picked = [];
      if (this.paged) { this.$nextTick(this.recheckList); }
    },
    batchEnabled(){ this.picked = []; },

    /* 【P297】父级说「刷新结束了」⇒ 把 Vant 的 v-model 同步回 false，
       Vant 的 value watcher 才会 setStatus(0)：
         · 头部下拉动画弹回原位
         · status 从 'loading' 复位 'normal'（否则 touchable=false，再也拉不动）
       ★ 这条链路断了就是「一直卡在加载中」的根因。 */
    refreshing(v) {
      if (!v) { this.innerRefreshing = false; }
    },
    /* 兜底：列表数据到位/清空都视为刷新结束（防父级漏置 refreshing 的场景）*/
    loading(v) {
      if (!v) { this.innerRefreshing = false; }
    },
  },
  methods: {
    keyOf(m)     { return m.sn || m.id; },
    isRead(m)    { return String(m.is_read) === '1' || Number(m.is_read) === 1; },
    isPicked(m)  { return this.picked.indexOf(String(this.keyOf(m))) >= 0; },
    roleText(m)  {
      /* 【P416】待办超时提醒：标签显示「超时」（配红色底），而不是「抄送」/「待办」 */
      if (m && m.scene === 'room_order_timeout') return '超时';
      if (Number(m.role) === 2) return '抄送';
      return m.role_name || (Number(m.type) === 2 ? '驳回' : '待办');
    },
    tagClass(m)  {
      if (m && m.scene === 'room_order_timeout') return 'mp-card__tag--timeout';
      return Number(m.role) === 2 ? 'mp-card__tag--cc' : 'mp-card__tag--todo';
    },
    sceneText(m) { return SCENE[m.scene] || m.scene || ''; },
    timeText(m)  { return (m.create_time || '').slice(5, 16); },

    /* ---------- 以下对齐销售端 client.vue 的消息语义（2026-10-11）---------- */

    /* 【P141】消息是否已作废：客户被改派他人后，后端会给原接收人的分配消息
       追加「（已失效）」后缀并置 status=0/is_action=1/is_read=1。
       ★ 用 content 后缀判定，而不是 status —— 否则改派产生的其它 status=0
         消息会被误判成失效。 */
    isInvalid(m) { return String((m && m.content) || '').indexOf('（已失效）') > -1; },

    /* 【P132·消息语义定版】严格按业务定义分状态：
         type=0 通知 → 只有「已读/未读」，**没有「已处理」概念**（完成判据 = is_read）
         type=1 待处理 → 完成判据 = is_action（0 待处理 / 1 已处理）
         type=2 驳回 → 结果类通知
       ⚠️ 历史坑：把 `is_action==='1' → 已处理` 放在 type 判定之前且不加 type 限定，
         而 `is_action` 列默认值就是 1 ⇒ 所有 type=0 通知被误标「已处理」，
         与角标（按 is_read 计未读）自相矛盾。 */
    statusOf(m) {
      const t = String((m && m.type !== undefined && m.type !== null) ? m.type : '');
      if (this.isInvalid(m))  { return { text: '已失效', cls: 'mp-st--danger' }; }
      if (t === '2')          { return { text: '已驳回', cls: 'mp-st--danger' }; }
      if (t === '1') {
        const ai = (m && m.is_action !== undefined && m.is_action !== null) ? String(m.is_action) : '';
        return ai === '1' ? { text: '已处理', cls: 'mp-st--done' } : { text: '待处理', cls: 'mp-st--wait' };
      }
      /* type=0 及其它 = 通知类：无「已处理」概念 */
      return { text: this.isRead(m) ? '已读' : '通知', cls: 'mp-st--note' };
    },

    /* 业务类型徽标：按 page_api 映射（默认「通知」）。父级可用 bizMap 覆盖。 */
    badgeOf(m) {
      const map = this.bizMap || {};
      const api = (m && m.page_api) || '';
      return map[api] || null;
    },
    badgeText(m) {
      const hit = this.badgeOf(m);
      return hit ? hit.text : '通知';
    },
    badgeStyle(m) {
      const hit = this.badgeOf(m);
      return hit ? { background: hit.bg, color: hit.color } : null;
    },

    /* 正文结构化抽取：把「【2026-01-01】【上午】支付 3500 元，折扣率 8.5」
       这类文案里的关键信息抽成标签行，最多 4 个。 */
    chipsOf(m) {
      const c = this.textOf(m);
      const out = [];
      if (!c) { return out; }
      const grab = (re) => { const m2 = c.match(re); return m2 ? m2[1] : ''; };
      const date = grab(/[【\[]([0-9]{4}-[0-9]{2}-[0-9]{2})[】\]]/);
      const span = grab(/[【\[](上午|下午|晚上|全天|白天)[】\]]/);
      const amount = grab(/支付\s*([0-9.]+)\s*元/);
      const price  = grab(/销售价格[:：]\s*([0-9.]+)/);
      const market = grab(/市场价格[:：]\s*([0-9.]+)/);
      const disc   = grab(/折扣率[:：]\s*([0-9.]+)/);
      if (date)  { out.push({ text: date }); }
      if (span)  { out.push({ text: span }); }
      if (amount)      { out.push({ text: '¥' + amount, key: true }); }
      else if (price)  { out.push({ text: '销售 ¥' + price, key: true }); }
      if (market) { out.push({ text: '市场 ¥' + market }); }
      if (disc)   { out.push({ text: '折扣 ' + disc }); }
      return out.slice(0, 4);
    },

    textOf(m)    { return m.content || (m.item && m.item.content) || ''; },
    /* 【P297】Vant 下拉到「松手触发刷新」时调用。
       职责：
         ① v-model（innerRefreshing）此时已被 Vant 自动置 true —— 保持不变；
         ② 立刻把「开始刷新」抛给父级，让它去发请求并最终把 refreshing 置 false。
       ★ 绝不能在这里把 innerRefreshing 立刻改回 false，
         否则 Vant 会认为刷新已结束、状态机反复重入（表现为头部动画抖动）。 */
    onPanelRefresh() {
      this.$emit('refresh');
    },

    /* 【P141 / P298】卡片点击：已失效的消息只提示原因、**不触发跳转**
       （客户已被改派他人，跳过去看到的是别人的客户），与销售端口径一致。 */
    onCardClick(m) {
      /* 【P141 / P298】已失效：只提示原因、**不跳转**（客户已被改派，那是别人的客户） */
      if (this.isInvalid(m)) {
        if (this.$toast) { this.$toast('该消息已失效（客户已被重新分配）'); }
        this.$emit('invalid', m);
        return;
      }
      /* 【P301】启用详情弹层时：打开弹层，并抛 open 让父级做「置已读 / 状态变更」
         （PRD：待处理、待阅驳回两个 Tab 单点进详情后自动完成阅读/处理）。 */
      if (this.showDetail) {
        this.detailRow = m;
        this.detailShow = true;
      }
      this.$emit('open', m);
    },

    /* 详情弹层里的「去处理」 */
    onDetailJump() {
      const row = this.detailRow;
      if (!row) { return; }
      this.detailShow = false;
      if (typeof this.onJump === 'function') { this.onJump(row); return; }
      this.$emit('action', row);
    },

    onSwitchTab(v)   { if (v !== this.tab) { this.$emit('tab', v); this.picked = []; } },
    onToggleOne(m)   {
      const k = String(this.keyOf(m)), i = this.picked.indexOf(k);
      if (i >= 0) this.picked.splice(i, 1); else this.picked.push(k);
    },
    onToggleAll(e)   {
      const on = e && e.target && e.target.checked;
      this.picked = on ? Array.from(new Set(this.list.map(m => String(this.keyOf(m))))) : [];
    },
    onAskBatch() {
      if (!this.picked.length) { this.$toast && this.$toast('请先勾选'); return; }
      this.dialogConfirm = true;
    },
    /* 【P385】★ 点确认后必须**立即关闭确认弹窗**
     *
     * 用户 10-15 16:48 反馈：「操作删除和已阅读后，确认弹窗还是存在，需要关闭。」
     *
     * 根因链条：
     *   ① ConfirmDialog.onConfirm() 只 $emit('confirm')，**从不 $emit('input', false)**
     *      —— 它的 v-model（MessagePanel 的 dialogConfirm）没有任何人写回 false；
     *   ② MessagePanel.doBatch() 也只 $emit('batch', …)，同样**没关 dialogConfirm**；
     *   ③于是无论删除/已读成功与否，弹窗都一直挂在页面上，
     *      只能靠点「取消」或点遮罩（此处close-on-click-overlay=false，遮罩也点不掉）关闭。
     *
     * 改法：doBatch 发出事件后同步 dialogConfirm=false +清空 picked。
     *   · 关闭时机选在「发出事件」之后而不是接口回调里 ——
     *     ConfirmDialog 的 loading 态由父级 busy 驱动，
     *     弹窗关掉后父级的 msgConfirming / busy 仍会照常转圈，
     *     进度反馈不会丢；而留在页面上反而挡住用户。
     *   · 清空 picked：避免删除成功后旧的勾选残留，下一次点批量会带上已失效的 sn。
     */
    doBatch() {
      const sns = this.picked.slice();
      this.dialogConfirm = false;
      this.picked = [];
      this.$emit('batch', { action: this.batchAction, sns });
    },

    /* 【P304】触底事件：把「请加载下一页」如实上抛，父级负责 page+1。
       ★ 组件自身**不改 loading**、也**不做并发拦截** ——
         van-list 的 loading 由父级 listLoading 驱动，
         若组件再拦一道，一旦父级状态没复位就会永久失灵（实测踩过：静默无请求）。 */
    onReachBottom() {
      if (!this.paged || this.finished) { return; }
      this.$emit('load-more');
    },

    /* 【P304】手动触发 van-list 自检。
       immediateCheck=false 时 Vant 不会自动 check()，
       我们在「数据到位 / 切 Tab」后主动调一次，
       让它重新计算 offsetHeight/scrollParent 并挂上滚动监听。 */
    recheckList() {
      const vl = this.$el ? this.$el.querySelector('.van-list') : null;
      if (!vl || !vl.__vue__ || typeof vl.__vue__.check !== 'function') { return; }
      try { vl.__vue__.check(); } catch (e) { /* 组件尚未就绪，下一轮 list 变化会再触发 */ }
    },
  },
}
</script>

<style scoped>
/* 【P304】paged 时 .mp 自身必须 overflow:hidden 并 min-height:0 ——
   否则内容会把 track 撑高，van-list 的 scrollHeight === clientHeight ⇒ 滑不动。 */
.mp { display:flex; flex-direction:column; min-height:100%; background:#f5f6f8; }
.mp--paged { overflow:hidden; min-height:0; height:100%; }

/* 顶部条 */
.mp-top{ display:flex; align-items:center; gap:10px; height:44px; padding:0 14px;
  background:#fff; border-bottom:.5px solid #ebedf0; flex:0 0 auto; }
.mp-top__back{ font-size:18px; color:#323233; }
.mp-top__t{ font-size:15px; font-weight:500; color:#1f2328; }
.mp-top__r{ margin-left:auto; font-size:12px; color:#2b5fd9; }

/* 分类 Tab：等宽铺满 + 触摸区 ~46px + 吸顶 */
.mp-tabs{ display:flex; background:#fff; border-bottom:.5px solid #ebedf0;
  position:sticky; top:0; z-index:6; flex:0 0 auto; }
.mp-tab{ position:relative; flex:1 1 0; min-width:0; padding:13px 2px 11px;
  text-align:center; border-bottom:2.5px solid transparent;
  -webkit-tap-highlight-color:transparent; }
.mp-tab:active{ background:#f2f3f5; }
.mp-tab--on{ border-bottom-color:#2b5fd9; }
.mp-tab__tx{ font-size:14px; font-weight:500; color:#8b949e; white-space:nowrap; }
.mp-tab--on .mp-tab__tx{ color:#2b5fd9; font-weight:600; }
.mp-tab__dot{ position:absolute; top:7px; left:calc(50% + 20px);
  min-width:17px; height:17px; padding:0 5px; border-radius:9px;
  background:#d93026; color:#fff; font-size:10.5px; line-height:17px;
  text-align:center; font-style:normal; box-sizing:border-box; }

/* 批量条 */
.mp-batch{ display:flex; align-items:center; gap:10px; margin:10px 12px; padding:9px 11px;
  background:#eef1f5; border-radius:9px; flex:0 0 auto; }
.mp-batch__all{ display:inline-flex; align-items:center; gap:6px; font-size:13px; color:#1f2328; }
.mp-batch__all input, .mp-card__pick input{ width:16px; height:16px; accent-color:#2b5fd9; }
.mp-batch__n{ font-size:12px; color:#5b636b; margin-left:auto; }
.mp-batch__n b{ color:#1f2328; }
.mp-batch__btn{ flex:0 0 auto; padding:7px 15px; border:none; border-radius:7px;
  background:#2b5fd9; color:#fff; font-size:12.5px; }
.mp-batch__btn:disabled{ background:#c9cdd4; }

/* 列表 */
.mp-list{ padding:10px 12px 0; }
.mp-err{ margin-bottom:8px; padding:8px 10px; border-radius:8px;
  background:#fef0f0; color:#a12a20; font-size:12px; }
.mp-card{ display:flex; gap:9px; padding:12px 13px; margin-bottom:8px;
  background:#fff; border:1px solid #ebedf0; border-radius:10px; }
.mp-card__pick{ flex:0 0 auto; display:flex; align-items:flex-start; padding-top:2px; }
.mp-card__dot{ flex:0 0 auto; width:7px; height:7px; border-radius:50%;
  margin-top:5px; background:transparent; }
.mp-card__dot--on{ background:#d93026; }
.mp-card__body{ flex:1; min-width:0; }
.mp-card__top{ display:flex; align-items:center; gap:6px; margin-bottom:4px; }
.mp-card__tag{ flex:0 0 auto; padding:1px 6px; border-radius:4px;
  font-size:10.5px; line-height:1.6; }
.mp-card__tag--todo{ background:#FAEEDA; color:#854F0B; }
.mp-card__tag--cc{ background:#EAF3DE; color:#3B6D11; }
/* 【P416】待办超时提醒：红底红字，配合左侧未读红点形成「红色点」提示 */
.mp-card__tag--timeout{ background:#FDE7E7; color:#C0392B; font-weight:700; }
.mp-card__scene{ font-size:10.5px; color:#8b949e; }
/* 【P298】业务状态标签（对齐销售端 msgStatus 三态 + 已失效） */
.mp-st{ flex:0 0 auto; padding:1px 6px; border-radius:4px; font-size:10.5px; line-height:1.6; }
.mp-st--wait{ background:#FAEEDA; color:#854F0B; }
.mp-st--done{ background:#EAF3DE; color:#3B6D11; }
.mp-st--note{ background:#EAF1FB; color:#185FA5; }
.mp-st--danger{ background:#FEF0F0; color:#A12A20; }
/* 【P141】已失效消息：整卡淡化，点击只提示不跳转 */
.mp-card--invalid{ opacity:.62; }
.mp-card__time{ font-size:10.5px; color:#8b949e; margin-left:auto; flex:0 0 auto; }
/* ★ 描述完整展示：pre-wrap 多行自适应，不加 line-clamp / ellipsis */
.mp-card__text{ font-size:12.5px; line-height:1.65; color:#1f2328;
  white-space:pre-wrap; word-break:break-word; overflow:visible; }
/* 【P298】正文结构化标签行（日期/时段/金额/折扣） */
.mp-card__chips{ display:flex; flex-wrap:wrap; gap:5px; margin-top:7px; }
.mp-chip{ padding:2px 7px; border-radius:11px; background:#f1f4f8; color:#5b636b; font-size:11px; }
.mp-chip--key{ background:#eef2fb; color:#2b5fd9; font-weight:500; }
/* 底部「去处理」 */
.mp-card__act{ display:inline-flex; align-items:center; gap:3px; margin-top:9px;
  padding:5px 11px; border-radius:14px; background:#eef2fb; color:#2b5fd9;
  font-size:12px; font-weight:600; }
.mp-card__act-ico{ font-size:12px; }

.mp-empty{ padding:40px 0 50px; text-align:center; }
.mp-empty__i{ font-size:26px; color:#c9cdd4; margin-bottom:8px; }
.mp-empty__t{ font-size:12.5px; color:#8b949e; }

/* 【P304】上拉分页到底文案（仅 paged=true 时渲染） */
.mp-nomore{ padding:14px 0 24px; text-align:center; font-size:12.5px; color:#c9cdd4; }
/* 【P301】消息详情弹层（仅启用 showDetail 时渲染，销售端沿用） */
.mp-detail{ display:flex; flex-direction:column; max-height:74vh; }
.mp-detail__hd{ display:flex; align-items:center; gap:8px; padding:14px 16px 8px;
  border-bottom:.5px solid #f0f1f3; flex:0 0 auto; }
.mp-detail__time{ margin-left:auto; font-size:11.5px; color:#8b949e; }
.mp-detail__close{ font-size:17px; color:#8b949e; }
.mp-detail__title{ padding:14px 16px; font-size:13.5px; line-height:1.75; color:#1f2328;
  white-space:pre-wrap; word-break:break-word; overflow-y:auto; flex:1 1 auto; }
.mp-detail__ft{ display:flex; gap:10px; padding:10px 16px 16px; flex:0 0 auto; }
.mp-detail__ft .van-button{ flex:1; }

.mp-foot{ display:flex; justify-content:space-between; padding:12px 16px 18px;
  font-size:11.5px; color:#8b949e; }
</style>



// WEBPACK FOOTER //
// src/components/MessagePanel.vue